import React, { useCallback, useMemo, useState } from 'react';
import './PushToBIWizard.css';
import StepConnection from './steps/StepConnection';
import StepQuery from './steps/StepQuery';
import StepPreview from './steps/StepPreview';
import StepSchedule from './steps/StepSchedule';
import StepConfirm from './steps/StepConfirm';
import { apiService } from '../../../services/api';
import { defaultScheduleZone } from '../shared/ScheduleZoneField';

// Default cadence depends on tool_hint per spec §6.2.
// extract_data → daily; query_structured_data may opt into hourly.
const DEFAULT_FORM = {
  connectionId: null,
  connectionKind: '',
  agentPrompt: '',
  toolHint: 'extract_data',
  schema: '',
  datasetIds: [],
  schedule: { frequency: 'daily', time: '08:00', dayOfWeek: 'monday' },
  refreshMode: 'replace',
  targetTable: '',
  targetDataset: '',
  automationName: '',
  description: '',
  timezone: null,
};

const STEPS = [
  { number: 1, label: 'Connection' },
  { number: 2, label: 'Query' },
  { number: 3, label: 'Preview' },
  { number: 4, label: 'Schedule' },
  { number: 5, label: 'Confirm' },
];

const PushToBIWizard = ({ isOpen, onClose, onShowNotification }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [draftId, setDraftId] = useState(null);
  const [previewRows, setPreviewRows] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const totalSteps = STEPS.length;

  const handleClose = useCallback(() => {
    setCurrentStep(1);
    setFormData(DEFAULT_FORM);
    setDraftId(null);
    setPreviewRows(null);
    setPreviewError(null);
    onClose();
  }, [onClose]);

  // Build the payload the backend expects on create.
  const buildPayload = useCallback(() => ({
    name: formData.automationName || formData.agentPrompt.slice(0, 50) || 'Push automation',
    description: formData.description || null,
    connection_id: formData.connectionId,
    agent_prompt: formData.agentPrompt,
    tool_hint: formData.toolHint,
    schema: formData.toolHint === 'extract_data' ? formData.schema || null : null,
    dataset_ids: Array.isArray(formData.datasetIds) ? formData.datasetIds : [],
    schedule: formData.schedule,
    refresh_mode: formData.refreshMode,
    target_table: formData.targetTable || null,
    target_dataset: formData.targetDataset || null,
    timezone: formData.timezone || defaultScheduleZone(),
  }), [formData]);

  // Ensure a draft exists before the Preview step so the backend has an
  // automation id to bind the preview rows to.
  const ensureDraft = useCallback(async () => {
    if (draftId) return draftId;
    const draftPayload = { ...buildPayload(), draft: true };
    const resp = await apiService.createPushAutomation(draftPayload);
    const id = resp?.id;
    if (!id) throw new Error('Backend did not return an automation id');
    setDraftId(id);
    return id;
  }, [draftId, buildPayload]);

  const runPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const id = await ensureDraft();
      const resp = await apiService.previewPushAutomation(id, { limit: 100 });
      const rows = resp?.rows ?? resp?.items ?? [];
      setPreviewRows(rows);
      return rows;
    } catch (err) {
      setPreviewRows(null);
      setPreviewError(err.message || 'Preview failed');
      throw err;
    } finally {
      setPreviewLoading(false);
    }
  }, [ensureDraft]);

  const handleNext = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      if (draftId) {
        await apiService.patchPushAutomation(draftId, { ...buildPayload(), draft: false });
      } else {
        await apiService.createPushAutomation(buildPayload());
      }
      onClose(true);
    } catch (err) {
      if (onShowNotification) onShowNotification(err.message || 'Failed to create push automation', 'error');
    } finally {
      setCreating(false);
    }
  };

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 1:
        return !!formData.connectionId;
      case 2:
        return !!formData.agentPrompt.trim() && !!formData.toolHint;
      case 3:
        // Spec §6.2 step 3: wizard cannot proceed without a successful preview.
        return Array.isArray(previewRows) && previewError === null;
      case 4:
        // Hourly only allowed for query_structured_data per spec.
        if (formData.schedule.frequency === 'hourly' && formData.toolHint !== 'query_structured_data') return false;
        return !!formData.refreshMode && (!!formData.targetTable || !!formData.targetDataset);
      case 5:
        return !!formData.automationName.trim();
      default:
        return false;
    }
  }, [currentStep, formData, previewRows, previewError]);

  if (!isOpen) return null;

  return (
    <div className="push-wizard-page">
      <div className="push-wizard-container">
        <div className="wizard-header">
          <div className="wizard-breadcrumb">
            <span onClick={handleClose}>Push to BI</span>
            <span className="wizard-breadcrumb-separator">/</span>
            <span>New push automation</span>
          </div>
          <div className="wizard-header-content">
            <h2 className="wizard-title">New push automation</h2>
            <button className="wizard-close" onClick={handleClose}>Cancel</button>
          </div>
        </div>

        <div className="wizard-steps" data-testid="push-wizard-steps">
          {STEPS.map((step) => (
            <div
              key={step.number}
              className={`wizard-step ${currentStep === step.number ? 'wizard-step-active' : ''} ${currentStep > step.number ? 'wizard-step-completed' : ''}`}
            >
              <span className="wizard-step-number">{step.number}.</span>
              <span className="wizard-step-label">{step.label}</span>
            </div>
          ))}
        </div>

        <div className="wizard-content">
          {currentStep === 1 && (
            <StepConnection formData={formData} onSetFormData={setFormData} />
          )}
          {currentStep === 2 && (
            <StepQuery formData={formData} onSetFormData={setFormData} />
          )}
          {currentStep === 3 && (
            <StepPreview
              formData={formData}
              previewRows={previewRows}
              previewError={previewError}
              previewLoading={previewLoading}
              onRunPreview={runPreview}
            />
          )}
          {currentStep === 4 && (
            <StepSchedule formData={formData} onSetFormData={setFormData} />
          )}
          {currentStep === 5 && (
            <StepConfirm formData={formData} onSetFormData={setFormData} previewRows={previewRows} />
          )}
        </div>

        <div className="wizard-footer">
          {currentStep > 1 && (
            <button className="wizard-button wizard-button-secondary" onClick={handleBack} disabled={creating}>
              Back
            </button>
          )}
          {currentStep === totalSteps ? (
            <button
              className="wizard-button wizard-button-primary"
              onClick={handleCreate}
              disabled={!canProceed || creating}
            >
              {creating ? 'Creating...' : 'Create push automation'}
            </button>
          ) : (
            <button
              className="wizard-button wizard-button-primary"
              onClick={handleNext}
              disabled={!canProceed}
              data-testid="push-wizard-next"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PushToBIWizard;
